import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';

export type UseIntersectionCallbacksOptions = Pick<
	IntersectionObserverInit,
	'root' | 'threshold'
> & {
	onEntry: (entry: IntersectionObserverEntry) => void;
	onLeave: (entry: IntersectionObserverEntry) => void;
	scrollMargin?: string;
};

/**
 * One `IntersectionObserver` for several targets. `onEntry` / `onLeave` are always
 * the latest callbacks without reconnecting the observer.
 *
 * Dependencies use a serialized snapshot of `ref.current` so the effect’s dependency
 * list stays a fixed arity even when `refs.length` changes. If multiple targets
 * stringify the same way, bump `refs.length` or ensure distinct roots in the DOM.
 */
export const useIntersectionCallbacks = (
	refs: ReadonlyArray<RefObject<Element | null>>,
	{ onEntry, onLeave, root, threshold = 0, scrollMargin }: UseIntersectionCallbacksOptions
) => {
	const onEntryRef = useRef(onEntry);
	const onLeaveRef = useRef(onLeave);

	useLayoutEffect(() => {
		onEntryRef.current = onEntry;
		onLeaveRef.current = onLeave;
	});

	const refsKey = refs.map(r => String(r.current)).join('|');

	useEffect(() => {
		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) onEntryRef.current(entry);
					else onLeaveRef.current(entry);
				});
			},
			{ root: root ?? null, threshold, scrollMargin } as any
		);

		for (const ref of refs) {
			const el = ref.current;
			if (el) observer.observe(el);
		}

		return () => observer.disconnect();
	}, [root, threshold, scrollMargin, refsKey]);
};
